import React, { useState } from 'react';
import { usePermissions } from '../PermissionsContext';
import './ControlPanel.css';


const ControlPanel = () => {
    const {
        permissions,
        nameOverride,
        updateNameOverride,
        clearNameOverride,
        addUserToLink,
        removeUserFromLink,
        addLink,
        removeLink,
        addUserToControlPanel,
        removeUserFromControlPanel,
        resetPermissions,
        setShowControlPanel,
        isLoading,
        error,
        refreshPermissions
    } = usePermissions();

    const [newUserInputs, setNewUserInputs] = useState({});
    const [overrideInput, setOverrideInput] = useState(nameOverride);
    const [newControlPanelUser, setNewControlPanelUser] = useState('');
    const [newLinkForm, setNewLinkForm] = useState({
        name: '',
        url: '',
        allowedUsers: ''
    });
    const [showAddLinkForm, setShowAddLinkForm] = useState(false);

    const handleAddUser = (linkId) => {
        const userName = newUserInputs[linkId]?.trim();
        if (userName) {
            addUserToLink(linkId, userName);
            setNewUserInputs({ ...newUserInputs, [linkId]: '' });
        }
    };

    const handleRemoveUser = (linkId, userName) => {
        removeUserFromLink(linkId, userName);
    };

    const handleAddControlPanelUser = () => {
        const userName = newControlPanelUser.trim();
        if (userName) {
            addUserToControlPanel(userName);
            setNewControlPanelUser('');
        }
    };

    const handleRemoveControlPanelUser = (userName) => {
        removeUserFromControlPanel(userName);
    };

    const handleSetOverride = () => {
        updateNameOverride(overrideInput.trim());
    };

    const handleClearOverride = () => {
        clearNameOverride();
        setOverrideInput('');
    };

    const handleInputChange = (linkId, value) => {
        setNewUserInputs({ ...newUserInputs, [linkId]: value });
    };

    const handleReset = () => {
        if (window.confirm('Are you sure you want to reset all permissions to default?')) {
            resetPermissions();
        }
    };

    const handleAddLink = () => {
        try {
            const allowedUsersArray = newLinkForm.allowedUsers
                .split(',')
                .map(u => u.trim())
                .filter(u => u.length > 0);
            
            addLink({
                name: newLinkForm.name,
                url: newLinkForm.url,
                allowedUsers: allowedUsersArray
            });
            
            setNewLinkForm({ name: '', url: '', allowedUsers: '' });
            setShowAddLinkForm(false);
        } catch (err) {
            alert('Error adding link: ' + err.message);
        }
    };

    const handleRemoveLink = (linkId, linkName) => {
        if (window.confirm(`Are you sure you want to remove the link "${linkName}"?`)) {
            removeLink(linkId);
        }
    };

    return (
        <div className="control-panel-overlay">
            <div className="control-panel">
                <div className="control-panel-header">
                    <h2>Permissions Control Panel</h2>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button 
                            className="btn btn-secondary"
                            onClick={refreshPermissions}
                            title="Refresh permissions from database"
                            disabled={isLoading}
                        >
                            Refresh
                        </button>
                        <button 
                            className="close-button" 
                            onClick={() => setShowControlPanel(false)}
                            title="Close"
                        >
                            ×
                        </button>
                    </div>
                </div>

                {/* Loading State */}
                {isLoading && (
                    <div className="control-section">
                        <p>Loading permissions...</p>
                    </div>
                )}

                {/* Error State */}
                {error && (
                    <div className="control-section" style={{ backgroundColor: '#fee', border: '1px solid #fcc' }}>
                        <p style={{ color: '#c00' }}>Error: {error}</p>
                        <button onClick={refreshPermissions} className="btn btn-secondary">
                            Retry
                        </button>
                    </div>
                )}

                {/* Name Override Section */}
                {!isLoading && (nameOverride === 'Cameron Ware' || (!nameOverride && permissions.controlPanelAccess?.includes('Cameron Ware'))) && (
                    <>
                <div className="control-section override-section">
                    <h3>Name Override (Testing)</h3>
                    <p className="section-description">
                        Test permissions as a different user without signing in again.
                    </p>
                    <div className="override-controls">
                        <input
                            type="text"
                            placeholder="Enter name to test as..."
                            value={overrideInput}
                            onChange={(e) => setOverrideInput(e.target.value)}
                            className="override-input"
                        />
                        <button 
                            onClick={handleSetOverride}
                            className="btn btn-primary"
                            disabled={!overrideInput.trim()}
                        >
                            Set Override
                        </button>
                        {nameOverride && (
                            <button 
                                onClick={handleClearOverride}
                                className="btn btn-secondary"
                            >
                                Clear Override
                            </button>
                        )}
                    </div>
                    {nameOverride && (
                        <div className="override-active">
                            Currently testing as: <strong>{nameOverride}</strong>
                        </div>
                    )}
                </div>
                </>
                )}

                {/* Control Panel Access Section */}
                {!isLoading && (
                <div className="control-section control-panel-access-section">
                    <h3>Control Panel Access</h3>
                    <p className="section-description">
                        Manage which users can see and access the control panel button. Leave empty to allow all users.
                    </p>
                    
                    <div className="users-list">
                        <strong>Allowed Users:</strong>
                        {permissions.controlPanelAccess && permissions.controlPanelAccess.length > 0 ? (
                            <ul>
                                {permissions.controlPanelAccess.map((user, index) => (
                                    <li key={index}>
                                        <span>{user}</span>
                                        <button
                                            onClick={() => handleRemoveControlPanelUser(user)}
                                            className="remove-user-btn"
                                            title="Remove user"
                                        >
                                            ×
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className="no-users">All users can access the control panel</div>
                        )}
                    </div>

                    <div className="add-user">
                        <input
                            type="text"
                            placeholder="Add user name..."
                            value={newControlPanelUser}
                            onChange={(e) => setNewControlPanelUser(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleAddControlPanelUser();
                                }
                            }}
                            className="add-user-input"
                        />
                        <button
                            onClick={handleAddControlPanelUser}
                            className="btn btn-small"
                            disabled={!newControlPanelUser.trim()}
                        >
                            Add User
                        </button>
                    </div>
                </div>
                )}

                {/* Permissions Management Section */}
                <div className="control-section permissions-section">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <div>
                            <h3 style={{ marginBottom: '5px' }}>Link Permissions</h3>
                            <p className="section-description" style={{ marginBottom: '0' }}>
                                Manage which users can see each link. Leave empty to allow all users.
                            </p>
                        </div>
                        {(nameOverride === 'Cameron Ware' || (!nameOverride && permissions.controlPanelAccess?.includes('Cameron Ware'))) && (
                            <button 
                                className="btn btn-primary"
                                onClick={() => setShowAddLinkForm(!showAddLinkForm)}
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                {showAddLinkForm ? 'Cancel' : '+ Add Link'}
                            </button>
                        )}
                    </div>

                    {showAddLinkForm && (
                        <div className="add-link-form" style={{
                            backgroundColor: '#f5f5f5',
                            padding: '20px',
                            borderRadius: '8px',
                            marginBottom: '20px',
                            border: '2px solid #4CAF50'
                        }}>
                            <h4 style={{ marginTop: '0', marginBottom: '15px' }}>Add New Link</h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>Link Name:</label>
                                    <input
                                        type="text"
                                        placeholder="e.g., Developer Progress Tracker"
                                        value={newLinkForm.name}
                                        onChange={(e) => setNewLinkForm({ ...newLinkForm, name: e.target.value })}
                                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>URL:</label>
                                    <input
                                        type="text"
                                        placeholder="e.g., https://example.com"
                                        value={newLinkForm.url}
                                        onChange={(e) => setNewLinkForm({ ...newLinkForm, url: e.target.value })}
                                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>Allowed Users (comma-separated, leave empty for all):</label>
                                    <input
                                        type="text"
                                        placeholder="e.g., Cameron Ware, John Doe"
                                        value={newLinkForm.allowedUsers}
                                        onChange={(e) => setNewLinkForm({ ...newLinkForm, allowedUsers: e.target.value })}
                                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                    />
                                </div>
                                <button
                                    onClick={handleAddLink}
                                    className="btn btn-primary"
                                    disabled={!newLinkForm.name.trim() || !newLinkForm.url.trim()}
                                    style={{ alignSelf: 'flex-start' }}
                                >
                                    Create Link
                                </button>
                            </div>
                        </div>
                    )}
                    
                    {permissions.links && permissions.links.length > 0 ? permissions.links.map(link => (
                        <div key={link.id} className="link-permissions">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ flex: 1 }}>
                                    <h4 style={{ margin: '0 0 5px 0' }}>{link.name}</h4>
                                    <div className="url-display">{link.url}</div>
                                </div>
                                {(nameOverride === 'Cameron Ware' || (!nameOverride && permissions.controlPanelAccess?.includes('Cameron Ware'))) && (
                                    <button
                                        onClick={() => handleRemoveLink(link.id, link.name)}
                                        className="btn btn-danger"
                                        style={{ marginLeft: '10px', flexShrink: 0 }}
                                        title="Delete this link"
                                    >
                                        Delete
                                    </button>
                                )}
                            </div>
                            
                            <div className="users-list">
                                <strong>Allowed Users:</strong>
                                {link.allowedUsers && link.allowedUsers.length > 0 ? (
                                    <ul>
                                        {link.allowedUsers.map((user, index) => (
                                            <li key={index}>
                                                <span>{user}</span>
                                                <button
                                                    onClick={() => handleRemoveUser(link.id, user)}
                                                    className="remove-user-btn"
                                                    title="Remove user"
                                                >
                                                    ×
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <div className="no-users">All users can access this link</div>
                                )}
                            </div>

                            <div className="add-user">
                                <input
                                    type="text"
                                    placeholder="Add user name..."
                                    value={newUserInputs[link.id] || ''}
                                    onChange={(e) => handleInputChange(link.id, e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            handleAddUser(link.id);
                                        }
                                    }}
                                    className="add-user-input"
                                />
                                <button
                                    onClick={() => handleAddUser(link.id)}
                                    className="btn btn-small"
                                    disabled={!newUserInputs[link.id]?.trim()}
                                >
                                    Add User
                                </button>
                            </div>
                        </div>
                    )) : (
                        <div className="no-users">No links configured</div>
                    )}
                </div>

                {/* Reset Section */}
                {!isLoading && (
                <div className="control-section reset-section">
                    <button onClick={handleReset} className="btn btn-danger">
                        Reset All Permissions to Default
                    </button>
                </div>
                )}
            </div>
        </div>
    );
};

export default ControlPanel;
